import { useLayoutEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';

/** Matches `w-11` below. Published as `--rail-w` so pages can inset themselves off it. */
const RAIL_W = 44;

/**
 * The one action that belongs to no project, parked on the right edge of the app.
 *
 * A narrow strip — icon plus a vertical label, no horizontal room spent — borrowed from the
 * collapsed shape of the KB browser's old per-project ingest panel, which used to own this edge.
 * That panel is gone: two ways to add, one pinned to a project and one not, sitting on the same
 * edge and looking alike, was the confusion rather than a convenience.
 *
 * Adding acts on the whole organization: with no destination chosen the agent decides which
 * projects the content belongs in, which is why this is reachable from **every** page — including
 * the KB browser, where the rail used to hide itself to stay out of that panel's way. It is a
 * companion to the sidebar's *Drop context* row, not a duplicate of it: the same destination, one
 * as a quiet nav entry and one as the standing affordance on the edge you are already reading at.
 * Deliberately the **same words** as that row and as the page itself — three labels for one
 * destination is how someone concludes they are three different features.
 */
export function AddToKbRail() {
  const { hasFeature } = useAuth();

  // Nothing to avoid any more — the KB browser's ingest panel is gone, so the rail is simply
  // wherever the knowledge base is visible at all.
  const visible = hasFeature('KB');

  // Same contract as `--sidebar-w`: the rail is `fixed`, so pages inset their own content with
  // `pr-[var(--rail-w)]` rather than each one being told a number.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--rail-w', visible ? `${RAIL_W}px` : '0px');
  }, [visible]);

  if (!visible) return null;

  return (
    <NavLink
      to="/add"
      title="Drop context"
      className={({ isActive }) =>
        cn(
          'fixed inset-y-0 right-0 w-11 z-30 flex flex-col items-center gap-3 py-3',
          // The label is set vertically, so its length is a height. Twelve characters clears any
          // ordinary window, but a short one must clip it rather than spill it past the icon or push
          // the rail out of view.
          'overflow-hidden border-l transition-colors',
          isActive
            ? 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-900'
            : 'bg-card border-border hover:bg-orange-50/60 dark:hover:bg-orange-950/20',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              'w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0 transition-colors',
              isActive ? 'bg-orange-500 text-white' : 'text-orange-500',
            )}
          >
            <Plus size={15} />
          </span>
          {/* `whitespace-nowrap` because the writing mode is vertical: without it the label wraps into
              a second *column* on a short window, which reads as two overlapping words rather than as
              truncation. `min-h-0` lets it shrink instead of forcing the rail taller than the
              viewport, and the overflow is hidden on the parent. */}
          <span
            className={cn(
              'text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors whitespace-nowrap min-h-0',
              isActive ? 'text-orange-600 dark:text-orange-400' : 'text-ink-faint',
            )}
            style={{ writingMode: 'vertical-rl' }}
          >
            Drop context
          </span>
        </>
      )}
    </NavLink>
  );
}
